import type { GroupedActivityMessage } from "../types/activity-message-types"
import type { ActivityMessage } from "@/api/models"
import type { ActivityGroupTitleInput } from "@/utils/activity-message-translator"

import { decodeGroupTitle } from "@/utils/activity-message-translator"

import { isClientInjectedActivity } from "./merge-client-injected-activities"

// System bookkeeping that fires before any real reasoning. Filtered from
// the user-facing thinking phase so non-RAG questions don't show a wall of
// generic phases. For RAG questions the meaningful activities (library
// searches, document reads, model reasoning) still come through.
const SYSTEM_NOISE_ACTIVITY_TYPES = new Set<string>([
  "preparing_context",
  "processing_request",
  "gathering_context",
  "context_ready",
  "preparing_tools",
  "preparing_response",
])

// Sentinel `activity_type` the durable-turn live snapshot inserts when it
// elides older activities off the tail to fit its byte budget
// (`ACTIVITY_TRUNCATED_ACTIVITY_TYPE` / `_durable_snapshot_body` in
// `services/chat_turn_runner.py`). Shaped as a genuine `ActivityMessage`
// (unlike the old bare `{ type: "activity", truncated: n }`, which had no
// `activity_type`/`message`/`timestamp` and could render as a broken row),
// so it is safely renderable even by code that does not special-case it —
// but `extractTruncatedActivitySentinel` below pulls it out to render a
// dedicated banner instead, mirroring Deep Explorer's own
// `{ event: "truncated", dropped: n }` handling (see
// `ActivityLog.tsx` / `activity_persistence.py`).
export const ACTIVITY_TRUNCATED_TYPE = "truncated"

export function isTruncatedActivitySentinel(message: ActivityMessage): boolean {
  return message.activity_type === ACTIVITY_TRUNCATED_TYPE
}

/** Split a `truncated` sentinel (if present) out of `messages`. At most one
 *  sentinel is expected per snapshot (`_durable_snapshot_body` inserts a
 *  single marker covering the whole elided run), but every occurrence is
 *  stripped from `rest` defensively. */
export function extractTruncatedActivitySentinel(messages: ActivityMessage[]): {
  sentinel: ActivityMessage | undefined
  rest: ActivityMessage[]
} {
  const sentinel = messages.find(isTruncatedActivitySentinel)
  if (!sentinel) return { sentinel: undefined, rest: messages }
  return { sentinel, rest: messages.filter((message) => !isTruncatedActivitySentinel(message)) }
}

export function isSystemNoise(message: ActivityMessage): boolean {
  if (SYSTEM_NOISE_ACTIVITY_TYPES.has(message.activity_type)) return true
  const translationKey = message.details?.translation_key
  if (typeof translationKey === "string" && SYSTEM_NOISE_ACTIVITY_TYPES.has(translationKey)) {
    return true
  }
  return false
}

export function filterSystemNoise(messages: ActivityMessage[]): ActivityMessage[] {
  return messages.filter((message) => !isSystemNoise(message))
}

/** Live status line: last *server* activity. Client-injected rows
 *  (e.g. `steer_applied`) stay in the accordion but must not pin the
 *  collapsed header — they are confirmations, not the current work phase. */
export function getLatestActivity(messages: ActivityMessage[]): ActivityMessage | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (!isClientInjectedActivity(messages[index])) {
      return messages[index]
    }
  }
  return undefined
}

export type TokenInfo = {
  total_tokens: number
  session_total_tokens?: number
  prompt_tokens?: number
  completion_tokens?: number
  session_prompt_tokens?: number
  session_completion_tokens?: number
}

export function getLatestTokenInfo(messages: ActivityMessage[]): TokenInfo | undefined {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const activity = messages[index]
    const rawTokenInfo = activity.details?.token_info as Record<string, unknown> | undefined

    if (!rawTokenInfo || typeof rawTokenInfo !== "object") {
      continue
    }

    const totalTokensValue = rawTokenInfo.total_tokens ?? rawTokenInfo.totalTokens
    const totalTokens = Number(totalTokensValue)

    if (!Number.isFinite(totalTokens)) {
      continue
    }

    const normalize = (value: unknown): number | undefined => {
      if (value == null) return undefined
      const numericValue = Number(value)
      return Number.isFinite(numericValue) ? numericValue : undefined
    }

    return {
      total_tokens: totalTokens,
      session_total_tokens: normalize(
        rawTokenInfo.session_total_tokens ?? rawTokenInfo.sessionTotalTokens
      ),
      prompt_tokens: normalize(rawTokenInfo.prompt_tokens ?? rawTokenInfo.promptTokens),
      completion_tokens: normalize(rawTokenInfo.completion_tokens ?? rawTokenInfo.completionTokens),
      session_prompt_tokens: normalize(
        rawTokenInfo.session_prompt_tokens ?? rawTokenInfo.sessionPromptTokens
      ),
      session_completion_tokens: normalize(
        rawTokenInfo.session_completion_tokens ?? rawTokenInfo.sessionCompletionTokens
      ),
    }
  }

  return undefined
}

/**
 * Groups consecutive activity messages into explicit group titles
 * If no custom titles are provided, returns empty array (no grouping)
 */
export function groupActivityMessages(
  messages: ActivityMessage[],
  defaultGroupTitle: string,
  translateGroupTitle?: (title: ActivityGroupTitleInput | undefined) => string | undefined
): GroupedActivityMessage[] {
  if (messages.length === 0) return []

  const filteredMessages = messages.filter((activity) => activity.activity_type !== "group_title")

  const hasCustomTitles = filteredMessages.some((activity) =>
    Boolean(decodeGroupTitle(activity.details?.group_title))
  )

  if (!hasCustomTitles) {
    return []
  }

  const firstCustomGroupTitle = filteredMessages.find((activity) =>
    Boolean(decodeGroupTitle(activity.details?.group_title))
  )?.details?.group_title

  const firstCustomTitle =
    translateGroupTitle?.(firstCustomGroupTitle) ||
    decodeGroupTitle(firstCustomGroupTitle)?.key ||
    defaultGroupTitle

  const groups: GroupedActivityMessage[] = []
  let currentGroup: GroupedActivityMessage | null = null
  let lastSeenGroupTitle = firstCustomTitle

  for (const activity of filteredMessages) {
    const rawGroupTitle = activity.details?.group_title
    const groupTitle =
      translateGroupTitle?.(rawGroupTitle) ||
      decodeGroupTitle(rawGroupTitle)?.key ||
      lastSeenGroupTitle

    lastSeenGroupTitle = groupTitle

    if (!currentGroup || currentGroup.title !== groupTitle) {
      currentGroup = {
        title: groupTitle,
        activities: [activity],
        agents: [],
      }
      groups.push(currentGroup)
    } else {
      currentGroup.activities.push(activity)
    }
  }

  groups.forEach((group) => {
    const agentMap = new Map<
      string,
      { name?: string; role?: string; type?: string; isLoading?: boolean }
    >()

    group.activities.forEach((activity) => {
      if (activity.details?.agent) {
        const agent = activity.details.agent
        const agentKey = agent.name

        if (agentKey) {
          if (!agentMap.has(agentKey)) {
            agentMap.set(agentKey, agent)
          } else {
            const existingAgent = agentMap.get(agentKey)!
            agentMap.set(agentKey, {
              ...existingAgent,
              ...agent,
            })
          }
        }
      }
    })

    group.agents = Array.from(agentMap.values())
  })

  return groups
}

export type RoutedModelInfo = { model: string; effort: string }

/**
 * Explorer sends the Auto model+effort routing decision (#2436) as
 * `activity_type: "thinking"` with `details.translation_key: "model_routed"`
 * (same convention as every other "thinking" sub-phase, e.g.
 * "gathering_context" — see isSystemNoise above). Check translation_key
 * first, activity_type as a fallback for any future caller that sends it
 * as a dedicated top-level type.
 */
export function isModelRoutedActivity(message: ActivityMessage): boolean {
  if (message.activity_type === "model_routed") return true
  return message.details?.translation_key === "model_routed"
}

/**
 * Extracts the Explorer Auto model+effort routing decision (#2436), if any
 * `model_routed` activity fired for this turn. Surfaced in the accordion
 * header (see ActivityMessageHeader) so it stays visible even once the
 * activity list collapses, instead of only appearing inside the expanded
 * item list.
 */
export function getRoutedModelInfo(messages: ActivityMessage[]): RoutedModelInfo | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const activity = messages[i]
    if (!isModelRoutedActivity(activity)) continue
    const model = typeof activity.details?.model === "string" ? activity.details.model : ""
    const effort = typeof activity.details?.effort === "string" ? activity.details.effort : ""
    if (!model && !effort) continue
    return { model, effort }
  }
  return undefined
}

/**
 * Calculates the total number of unique sources from all activity messages
 * Counts unique results from web_search_results, search_results activities, and relevant_links from research activities
 */
export function getTotalSourcesCount(messages: ActivityMessage[]): number {
  const breakdown = getSourcesByType(messages)
  return Object.values(breakdown).reduce((sum, count) => sum + count, 0)
}

export type SourceType = "web" | "documents" | "research"

export type SourcesByType = Partial<Record<SourceType, number>>

// Classifies an activity message into a source bucket by activity_type and/or
// details.translation_key. Returns undefined for messages that don't carry
// source payloads.
function classifySource(message: ActivityMessage): SourceType | undefined {
  const at = message.activity_type
  const rawKey = message.details?.translation_key
  const tk = typeof rawKey === "string" ? rawKey : undefined

  // Web — search result URLs and scraped pages
  if (at === "web_search_results" || at === "browse_results") return "web"
  if (tk === "web_search_results_found" || tk === "browse_results") return "web"

  // Research — deep-research aggregation with relevant_links
  if (at === "research") return "research"

  // Documents — library / knowledge-base / M365 files / doc-internal search
  if (at === "search_results") return "documents"
  if (
    tk === "navigator_search_completed_singular" ||
    tk === "navigator_search_completed_plural" ||
    tk === "document_insights_search_completed_singular" ||
    tk === "document_insights_search_completed_plural" ||
    tk === "doc_internal_search_completed" ||
    tk === "m365_files_found" ||
    tk === "file_search_complete"
  ) {
    return "documents"
  }

  return undefined
}

// Returns a stable uniqueness key for a result object regardless of whether
// the backend populated a URL (web), a title+library pair (library search),
// or an id (document_references / M365 files).
function sourceKeyFromResult(result: unknown): string | undefined {
  if (typeof result === "string" && result.length > 0) return result
  if (!result || typeof result !== "object") return undefined
  const record = result as Record<string, unknown>

  const url = record.url
  if (typeof url === "string" && url.length > 0) return url

  const id = record.id ?? record.documentId ?? record.sourceId
  if (typeof id === "string" && id.length > 0) return id

  const title = record.title
  if (typeof title === "string" && title.length > 0) {
    const scope = record.library ?? record.source ?? record.index ?? ""
    return `${typeof scope === "string" ? scope : ""}::${title}`
  }

  const displayTitle = record.display_title
  if (typeof displayTitle === "string" && displayTitle.length > 0) return displayTitle

  return undefined
}

// Pushes every discoverable source identifier from an activity message's
// details into the given bucket. Handles results[], urls[], relevant_links[],
// and document_references[] shapes emitted by different backend code paths.
function collectSourceKeys(bucket: Set<string>, message: ActivityMessage): void {
  const details = message.details as Record<string, unknown> | undefined
  if (!details) return

  const results = details.results
  if (Array.isArray(results)) {
    for (const entry of results) {
      const key = sourceKeyFromResult(entry)
      if (key) bucket.add(key)
    }
  }

  const urls = details.urls
  if (Array.isArray(urls)) {
    for (const entry of urls) {
      if (typeof entry === "string" && entry.length > 0) bucket.add(entry)
    }
  }

  const relevantLinks = details.relevant_links
  if (Array.isArray(relevantLinks)) {
    for (const entry of relevantLinks) {
      if (typeof entry === "string" && entry.length > 0) bucket.add(entry)
    }
  }

  const documentReferences = details.document_references
  if (Array.isArray(documentReferences)) {
    for (const ref of documentReferences) {
      const key = sourceKeyFromResult(ref)
      if (key) bucket.add(key)
    }
  }
}

/**
 * Returns unique source counts grouped by type (web / documents / research).
 * Used by the collapsed activity header to render per-type source chips so
 * the user can see at a glance what kinds of sources are being consulted.
 */
export function getSourcesByType(messages: ActivityMessage[]): SourcesByType {
  const buckets: Record<SourceType, Set<string>> = {
    web: new Set<string>(),
    documents: new Set<string>(),
    research: new Set<string>(),
  }

  // Some activity types (e.g. library_search_citations_results) only expose
  // a `count` without individual result entries. Fall back to the maximum
  // reported count so we still contribute a non-zero tally to the bucket.
  const countFallbacks: Record<SourceType, number> = {
    web: 0,
    documents: 0,
    research: 0,
  }

  for (const message of messages) {
    const bucketKey = classifySource(message)
    if (!bucketKey) continue

    const before = buckets[bucketKey].size
    collectSourceKeys(buckets[bucketKey], message)

    if (buckets[bucketKey].size === before) {
      // No extractable identifiers — use the reported count as a floor.
      const rawCount = (message.details as Record<string, unknown> | undefined)?.count
      if (typeof rawCount === "number" && rawCount > countFallbacks[bucketKey]) {
        countFallbacks[bucketKey] = rawCount
      }
    }
  }

  const result: SourcesByType = {}
  for (const type of ["web", "documents", "research"] as SourceType[]) {
    const extracted = buckets[type].size
    const fallback = countFallbacks[type]
    const total = Math.max(extracted, fallback)
    if (total > 0) result[type] = total
  }
  return result
}
